<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['close', 'capture', 'captures', 'error']);

const { t } = useI18n();
const useAlertMessage = useAlert();

const videoRef = ref(null);
const canvasRef = ref(null);
const stream = ref(null);
const capturedImages = ref([]);
const isCapturing = ref(false);
const hasCamera = ref(false);
const cameraError = ref(null);

const startCamera = async () => {
  try {
    // Check if getUserMedia is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('Camera API not supported in this browser');
    }

    const constraints = {
      video: {
        facingMode: 'environment', // Use back camera on mobile
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
    };

    const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

    stream.value = mediaStream;
    hasCamera.value = true;
    cameraError.value = null;

    if (videoRef.value) {
      videoRef.value.srcObject = mediaStream;
      await videoRef.value.play();
    }
  } catch (error) {
    console.error('Camera access error:', error);
    cameraError.value = error.message;
    hasCamera.value = false;

    let errorMessage = t('BUSINESS_CARD.CAMERA.ERRORS.ACCESS_DENIED');
    if (error.name === 'NotAllowedError') {
      errorMessage = t('BUSINESS_CARD.CAMERA.ERRORS.PERMISSION_DENIED');
    } else if (error.name === 'NotFoundError') {
      errorMessage = t('BUSINESS_CARD.CAMERA.ERRORS.NO_CAMERA');
    } else if (error.name === 'NotSupportedError') {
      errorMessage = t('BUSINESS_CARD.CAMERA.ERRORS.NOT_SUPPORTED');
    }

    useAlertMessage.error(errorMessage);
    emit('error', errorMessage);
  }
};

const captureImage = () => {
  if (!videoRef.value || !canvasRef.value || !stream.value) return;

  isCapturing.value = true;

  try {
    const canvas = canvasRef.value;
    const video = videoRef.value;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to blob
    canvas.toBlob(
      blob => {
        if (!blob) {
          isCapturing.value = false;
          return;
        }

        const timestamp = Date.now();
        const file = new File([blob], `business-card-${timestamp}.jpg`, {
          type: 'image/jpeg',
        });

        if (props.multiple) {
          capturedImages.value.push(file);
          useAlertMessage.success(
            t('BUSINESS_CARD.CAMERA.CAPTURED', {
              count: capturedImages.value.length,
            })
          );
        } else {
          emit('capture', file);
          stopCamera();
          emit('close');
        }

        isCapturing.value = false;
      },
      'image/jpeg',
      0.92 // Quality
    );
  } catch (error) {
    console.error('Capture error:', error);
    useAlertMessage.error(t('BUSINESS_CARD.CAMERA.ERRORS.CAPTURE_FAILED'));
    isCapturing.value = false;
  }
};

const finishCapture = () => {
  if (capturedImages.value.length > 0) {
    emit('captures', capturedImages.value);
  }
  stopCamera();
  emit('close');
};

const stopCamera = () => {
  if (stream.value) {
    stream.value.getTracks().forEach(track => {
      track.stop();
    });
    stream.value = null;
  }
  hasCamera.value = false;
};

const removeCaptured = index => {
  capturedImages.value.splice(index, 1);
};

watch(
  () => props.open,
  isOpen => {
    if (isOpen) {
      startCamera();
    } else {
      stopCamera();
      capturedImages.value = [];
    }
  }
);

onMounted(() => {
  if (props.open) {
    startCamera();
  }
});

onUnmounted(() => {
  stopCamera();
});
</script>

<template>
  <Dialog
    :title="t('BUSINESS_CARD.CAMERA.TITLE')"
    :description="t('BUSINESS_CARD.CAMERA.DESCRIPTION')"
    :open="open"
    @close="
      stopCamera();
      emit('close');
    "
  >
    <template #default>
      <div class="flex flex-col gap-4">
        <!-- Camera Error State -->
        <div
          v-if="cameraError"
          class="flex flex-col items-center justify-center p-8 rounded-lg bg-n-ruby-2 border border-n-ruby-6"
        >
          <Icon icon="i-lucide-camera-off" class="size-12 text-n-ruby-9 mb-4" />
          <p class="text-sm font-medium text-n-ruby-11">
            {{ cameraError }}
          </p>
          <p class="text-xs text-n-ruby-10 mt-2">
            {{ t('BUSINESS_CARD.CAMERA.ERRORS.HELP_TEXT') }}
          </p>
        </div>

        <!-- Camera Preview -->
        <div
          v-else-if="hasCamera"
          class="relative flex flex-col items-center gap-4"
        >
          <div class="relative w-full max-w-md">
            <!-- Mirror video for better UX -->
            <video
              ref="videoRef"
              autoplay
              playsinline
              class="w-full rounded-lg bg-n-slate-3"
              style="transform: scaleX(-1)"
            />

            <!-- Scan Frame Overlay -->
            <div class="absolute inset-0 pointer-events-none">
              <div
                class="absolute inset-4 border-2 border-n-blue-9 rounded-lg"
                style="box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.3)"
              />
              <div
                class="absolute top-4 left-4 right-4 text-center text-white text-sm font-medium"
              >
                {{ t('BUSINESS_CARD.CAMERA.ALIGN_CARD') }}
              </div>
            </div>

            <canvas ref="canvasRef" class="hidden" />
          </div>

          <!-- Captured Images Preview -->
          <div
            v-if="capturedImages.length > 0"
            class="flex flex-wrap gap-2 w-full"
          >
            <div
              v-for="(image, index) in capturedImages"
              :key="index"
              class="relative"
            >
              <img
                :src="URL.createObjectURL(image)"
                class="w-20 h-20 object-cover rounded border border-n-weak"
              />
              <Button
                icon="i-lucide-x"
                variant="ghost"
                size="xs"
                color="ruby"
                class="absolute -top-1 -right-1"
                @click="removeCaptured(index)"
              />
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-else class="flex items-center justify-center p-8">
          <Icon
            icon="i-lucide-loader-2"
            class="size-8 text-n-slate-11 animate-spin"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <div class="text-sm text-n-slate-11">
          <span v-if="multiple && capturedImages.length > 0">
            {{
              t('BUSINESS_CARD.CAMERA.CAPTURED_COUNT', {
                count: capturedImages.length,
              })
            }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <Button
            variant="faded"
            color="slate"
            :label="t('BUTTON.CANCEL')"
            @click="
              stopCamera();
              emit('close');
            "
          />
          <Button
            v-if="multiple"
            :label="t('BUSINESS_CARD.CAMERA.CAPTURE_BUTTON')"
            :disabled="!hasCamera || isCapturing"
            :is-loading="isCapturing"
            @click="captureImage"
          />
          <Button
            v-if="multiple && capturedImages.length > 0"
            :label="t('BUSINESS_CARD.CAMERA.FINISH_BUTTON')"
            @click="finishCapture"
          />
          <Button
            v-if="!multiple"
            :label="t('BUSINESS_CARD.CAMERA.CAPTURE_BUTTON')"
            :disabled="!hasCamera || isCapturing"
            :is-loading="isCapturing"
            @click="captureImage"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
video {
  max-height: 60vh;
  object-fit: cover;
}
</style>
